/**
 * VX L2 — 현장 이미지 리졸버 «순수» 부분 (최종지시서_VX_20261012 v2.1 · L2 · §N).
 *
 * 우선순위는 이 파일의 `resolveFromRows` «하나» 가 정한다:
 *   ① 공식(hero_image_url · 라이선스 통과분 — 상세 히어로·카드와 같은 판정 함수 licensedHeroPhoto)
 *   ② 생성(site_images · visible AND site_visible)
 *   ③ 위성 사진 카드(VX-3 C3 — apt_sites.satellite_image_url 보유 AND site_sat_cards.ok. «화면 표시 전용»:
 *      og·JSON-LD·sitemap·색인 썸네일 주소는 위성에서 종전과 같은 값(null/[])을 낸다. 주소는 /img-sat/legacy/{site_id}.webp(noindex))
 *   ④ 카드(기존 체인 그대로 — 이 모듈은 아무것도 내지 않는다)
 * og·JSON-LD·sitemap·갤러리·히어로·썸네일이 전부 이 결과를 읽는다 — 화면에 없는 이미지를 sitemap·JSON-LD 가 내보내는 일 0.
 *
 * ⛔ 이 파일은 DB·네트워크·fs·env 를 부르지 않는다(값은 인자로만 온다). 서버 조회는 `./server.ts`.
 * ⛔ 생성 이미지 주소는 Storage «키» 에서만 만든다(`sites/…` → `/img/…`). URL 이 통째로 들어온 값은 버린다 —
 *    그래서 `supabase.co` 직링크가 이 모듈 밖으로 나갈 길이 없다(G-VX2).
 */
import { licensedHeroPhoto, type DerivedSiteLike, type HeroPolicy } from '@/lib/apt/detail-derived';
import { satRefPathFromLegacyUrl, satelliteAltOf, SAT_REF_SIZE } from './compare';
import { CANONICAL_ORIGIN } from '@/lib/cl-core/config';
import type { SiteScale } from '@/lib/imagegen/scale/types';
import { nameFromStoredCaption, scaleBadgeText, siteNameOf, withScaleText, SCALE_SHOT_LABEL } from './scale-caption';
import { VWORLD_ATTRIBUTION } from '@/lib/attribution/vworld';

/* ───────────────────────────── 계약(L1 L2_IFACE) ───────────────────────────── */

export const SITE_SHOTS = ['S1_aerial_day', 'S2_aerial_golden', 'S3_eye_level', 'S4_night'] as const;
export type SiteShot = (typeof SITE_SHOTS)[number];

/** kadeora 화면·색인에 쓰는 파생본만. insta_4x5·ext_* 는 외부 채널 전용(L5) — 여기서 읽지 않는다. */
export const SCREEN_VARIANTS = {
  master_jpg: { width: 2048, height: 1152, type: 'image/jpeg' },
  og_1200x630_jpg: { width: 1200, height: 630, type: 'image/jpeg' },
  thumb_1x1_jpg: { width: 1080, height: 1080, type: 'image/jpeg' },
  hero_21x9_webp: { width: 1920, height: 823, type: 'image/webp' },
  hero_4x3_webp: { width: 1440, height: 1080, type: 'image/webp' },
  gallery_webp: { width: 1600, height: 900, type: 'image/webp' },
} as const;
export type ScreenVariant = keyof typeof SCREEN_VARIANTS;

/** §N-4 — 현장당 갤러리·sitemap·JSON-LD 상한. */
export const MAX_SHOTS = 4;
export const AI_CREDIT_TEXT = 'AI 생성 이미지 · KADEORA';
export const IMG_PREFIX = '/img';

export type HeroKind = 'official' | 'generated' | 'satellite' | 'card';

/**
 * VX-4 §8-3 — 「입주가 시작된 뒤」 단계(기축 계열 전부). 이 단계의 현장에는 상상도를 «생성 등급» 으로 내지 않는다(오인 방지).
 *   ⚠️ 단계 집합은 이 상수 한 곳이다 — 리졸버 가드(resolveFromRows) · 적재기의 내림(imagegen/lifecycle) · 블로그 캡션 전환이 같이 읽는다.
 *   DB 쪽 같은 집합: 뷰 v_site_channel_images(마이그레이션 vx4_lifecycle). 바꾸면 그 뷰도 같이 바꾼다.
 *   (`detail-derived.isGichuk` 은 move_in_started 를 뺀 둘뿐인 «다른 집합» 이다 — 위성 히어로용. 합치지 않는다.)
 */
export const COMPLETED_STAGES = ['move_in_started', 'post_move_in', 'landmark_active'] as const;
export function isCompletedStage(stage: string | null | undefined): boolean {
  return typeof stage === 'string' && (COMPLETED_STAGES as readonly string[]).includes(stage);
}
/** site_images.hidden_reason 값(VX-4). */
export const HIDDEN_COMPLETED = 'completed';
export const HIDDEN_EXCLUDED = 'excluded';
/** 상세 갤러리 맨 끝 「분양 당시 AI 상상도」 줄 — 제목과 «고정» 캡션. */
export const ARCHIVED_SHOTS_TITLE = '분양 당시 AI 상상도';
export const ARCHIVED_SHOT_CAPTION = '분양 당시 AI 상상도 · 실제와 다를 수 있음';

/** VX-3 D2 — 위성 사진 카드의 출처 표기(의무). KdMediaImage.credit 로 나간다. */
export const SATELLITE_CREDIT_TEXT = VWORLD_ATTRIBUTION; // post1 1b — 단일 원천 lib/attribution/vworld(링크형 문구)
/** 흐린 미리보기 data URI 상한(계약 600바이트 → base64 ≈ 800자 + 머리). 넘으면 버린다. */
export const BLUR_DATA_MAX = 2048;

/** site_images 행(조회 열만). */
export interface SiteImageRow {
  site_id: string;
  shot: string;
  variants: Record<string, unknown> | null;
  alt_ko: string | null;
  caption_ko: string | null;
  version_hash?: string | null;
  visible?: boolean | null;
  /** VX-3 D3 — 흐린 미리보기(≈16px data URI). 열이 없거나 비면 undefined/null. */
  blur_data?: string | null;
  /** VX-4 §8-3 — 내려간 사유(completed · excluded · null). 상세 조회만 싣는다. */
  hidden_reason?: string | null;
}

/** site_sat_cards 행(조회 열만) — VX-3 C1. */
export interface SatCardRow {
  site_id?: string | null;
  ok?: boolean | null;
  reason?: string | null;
  blur_data?: string | null;
}

/** 위성 사진 카드 1장 — 자체 도메인 noindex 상대경로만 담는다(직링크 없음). */
export interface SatelliteRef {
  /** /img-sat/legacy/{site_id}.webp */
  path: string;
  width: number;
  height: number;
  blur: string | null;
}

export interface ImgRef {
  /** 같은 출처 상대경로 `/img/{site_id}/{shot}-{variant}-{hash}.{ext}` — 화면 표시(webp source)용. */
  path: string;
  /** 절대 URL `https://kadeora.app/img/…` — 색인 경로(og·JSON-LD·sitemap·<img src>)용. */
  url: string;
  width: number;
  height: number;
  type: 'image/jpeg' | 'image/webp';
}

/** 생성 컷 1장 — 색인 3종(JPEG)은 필수, 화면용 webp 는 있으면. */
export interface GeneratedShot {
  shot: SiteShot;
  alt: string;
  caption: string;
  versionHash: string | null;
  master: ImgRef;
  og: ImgRef;
  thumb: ImgRef;
  hero21x9: ImgRef | null;
  hero4x3: ImgRef | null;
  gallery: ImgRef | null;
  /** VX-3 D3 — 흐린 미리보기 data URI(검증 통과분). 없으면 키 자체가 없다. */
  blur?: string | null;
}

export interface ResolvedSiteImage {
  kind: HeroKind;
  /** 게이트(app_config image.site_visible 또는 로컬 오버라이드)가 열려 있었는가 — 계측 표식의 조건. */
  gateOpen: boolean;
  /** kind='official' 일 때만 — 라이선스 통과한 hero_image_url. */
  officialUrl: string | null;
  /** kind='generated' 일 때만 채워진다(최대 4, SITE_SHOTS 순). 그 밖에는 빈 배열. */
  shots: GeneratedShot[];
  /** shots[0] — 히어로·og·대표 썸네일. */
  hero: GeneratedShot | null;
  /** kind='satellite' 일 때만(VX-3). 그 밖에는 키 자체가 없다 — 화면 표시 전용. */
  satellite?: SatelliteRef | null;
  /**
   * VX-4 §8-3 — 입주 시작 뒤 내려간 컷(visible=false · hidden_reason='completed'). «상세 갤러리 맨 끝» 의 「분양 당시 AI 상상도」 줄 전용.
   * ⛔ 히어로·og·JSON-LD·sitemap·카드·썸네일은 이 배열을 읽지 않는다(그 출력들은 kind/shots/hero 만 본다). 없으면 키 자체가 없다.
   */
  archived?: GeneratedShot[];
  /**
   * vx_scale1 E · v2 §J — 사실 배지 「3개동 · 최고 15층 · 1,234세대」(확인된 값만). kind='generated' 이고 규모 사실이 있을 때만 키가 있다.
   */
  scaleBadge?: string;
}

/**
 * 화면 레인 공용 미디어 모양(L4-0 MEDIA_IFACE 호환 가정):
 *   `{ kind, jpg, webp?, alt, caption?, width, height, shot? }` — jpg 는 절대 URL(색인), webp 는 상대경로(표시).
 */
export interface SiteMedia {
  kind: HeroKind;
  jpg: string;
  webp?: string;
  alt: string;
  caption?: string;
  width: number;
  height: number;
  shot?: SiteShot;
  /** VX-3 D3 — 흐린 미리보기 data URI. */
  blur?: string | null;
  /** VX-3 D2 — 위성 출처(lib/attribution/vworld). satellite 에만. */
  credit?: string;
  /** VX-3 D3 — 있는 파생본으로 짠 picture source(화면 표시용 webp · 상대경로). mediaOf 의 hero 슬롯에만. */
  sources?: Array<{ srcSet: string; media?: string; type?: string; width?: number; height?: number }>;
}

/** 공식 판정에 쓰는 apt_sites 부분 행. id 는 site_images 조회 키. */
export type SiteImageSiteLike = DerivedSiteLike & { id?: string | null };

/* ───────────────────────────── 게이트 ───────────────────────────── */

/**
 * 로컬 오버라이드 가드 — `IMAGE_SITE_VISIBLE_OVERRIDE==='1'` 이고 `VERCEL_ENV!=='production'` 일 때만 참.
 * ⛔ 프로덕션에서는 env 가 실수로 들어가도 열리지 않는다(G-VX5).
 */
export function siteVisibleOverride(env: Record<string, string | undefined>): boolean {
  return env.IMAGE_SITE_VISIBLE_OVERRIDE === '1' && env.VERCEL_ENV !== 'production';
}

/** jsonb 불리언 — true·"true"·1·"1"·"on"·"yes" 만 연다. 그 밖(누락·null·이상값)은 닫힘. */
export function parseVisibleFlag(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (typeof v === 'string') return ['true', '1', 'on', 'yes'].includes(v.trim().toLowerCase().replace(/^"|"$/g, ''));
  return false;
}

export function gateOpenOf(configValue: unknown, env: Record<string, string | undefined>): boolean {
  return siteVisibleOverride(env) || parseVisibleFlag(configValue);
}

/* ───────────────────────────── URL ───────────────────────────── */

const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const KEY_RE = new RegExp(`^sites/(${UUID})/([A-Za-z0-9_]+)-([a-z0-9_]+)-([A-Za-z0-9]+)\\.(jpg|webp)$`);

/**
 * Storage 키 → 공개 상대경로. `sites/{site_id}/{shot}-{variant}-{hash}.{ext}` 모양만 통과한다.
 * ⛔ URL·다른 버킷 경로·비ASCII·상위 경로(`..`)는 전부 null — 직링크가 화면으로 새지 않는다.
 */
export function publicImgPath(storageKey: unknown): string | null {
  if (typeof storageKey !== 'string') return null;
  const k = storageKey.trim();
  if (!KEY_RE.test(k)) return null;
  return `${IMG_PREFIX}/${k.slice('sites/'.length)}`;
}

/** 상대경로 → 절대 URL. origin 꼬리 슬래시는 걷는다. */
export function absImgUrl(path: string, origin: string): string {
  return `${origin.replace(/\/+$/, '')}${path}`;
}

/* ───────────────────────────── 이름 별칭 주소(img-names) ─────────────────────────────
 * 이미지 검색(네이버 이미지 탭 · 구글 이미지)이 «현장명» 질의에 우리 상상도를 잡도록, 색인 JPEG 주소의 파일명에 현장명·컷 이름을 싣는다.
 *
 *   정본(Storage 키 그대로)  /img/{site_id}/{shot}-{variant}-{hash}.jpg
 *   별칭(색인 출력)          /img/{site_id}/{현장slug}-{컷 한글}-상상도--{shot}-{variant}-{hash}.jpg   (한글은 퍼센트 인코딩)
 *   채널 변형본              /img/{site_id}/ch/{…}--{channel}-{shot}-{ratio}-{hash}.jpg
 *
 * 같은 객체다 — next.config rewrites 의 별칭 규칙이 `--` 앞(이름)을 버리고 `sites/{site_id}/{key}.jpg` 로 보낸다(함수 0회 · 리다이렉트 0 ·
 * 같은 바이트 · 같은 Content-Type · /img 헤더 규칙(1년 immutable · X-Robots-Tag all) 그대로). 정본 주소도 그대로 열린다(이미 색인된 주소 보존).
 * ⚠️ `--` 는 구분자다 — 이름 조각(imgSeoName)은 연속 하이픈을 하나로 접으므로 이름 안에 `--` 가 생기지 않는다.
 * ⚠️ 별칭은 JPEG(색인 대상)에만 붙인다. webp 파생본은 화면 표시 전용(noindex)이라 정본 경로 그대로다.
 * ⚠️ /img 주소를 «해석» 하는 함수는 canonicalImgPath 로 정본으로 되돌린 뒤 읽는다(파일명 규칙 판정이 별칭에 흔들리지 않게).
 */
export const IMG_ALIAS_SEP = '--';
/** 별칭 파일명 꼬리(「AI 상상도」 표지와 같은 낱말 — 「조감도」 는 공식 이미지 전용이라 쓰지 않는다). */
export const IMG_ALIAS_TAIL = '상상도';
/** 이름 조각 상한(코드포인트). URL 이 지나치게 길어지지 않게. */
export const IMG_SEO_NAME_MAX = 40;
const ALIAS_KEY_SRC = 'S[0-9]_[a-z_]+-[a-z0-9_]+-[A-Za-z0-9]+';
const CANON_IMG_RE = new RegExp(`^/img/(${UUID})/(ch/[a-z0-9]+-|)(${ALIAS_KEY_SRC})\\.jpg$`);
const ALIAS_IMG_RE = new RegExp(`^/img/(${UUID})/(ch/|)([^/]+?)${IMG_ALIAS_SEP}((?:[a-z0-9]+-)?${ALIAS_KEY_SRC})\\.jpg$`);

/** next.config rewrites 가 쓰는 원문(정규식 조각) — 설정과 시험이 같은 값을 읽는다. */
export const IMG_ALIAS_REWRITE = Object.freeze({
  uuid: UUID,
  key: ALIAS_KEY_SRC,
  chKey: `[a-z0-9]+-${ALIAS_KEY_SRC}`,
});

/** 현장 이름·slug → 파일명 조각. 글자·숫자 밖은 하이픈 하나로 · 앞뒤 하이픈 제거 · NFC · 상한. 비면 null. */
export function imgSeoName(name: unknown): string | null {
  const s = String(name ?? '').normalize('NFC').replace(/[^\p{L}\p{N}]+/gu, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  if (!s) return null;
  const cut = Array.from(s).slice(0, IMG_SEO_NAME_MAX).join('').replace(/-+$/, '');
  return cut || null;
}

/** 현장 행 → 별칭 이름(slug > 표시명 > 이름). 없으면 null. */
export function imgSeoNameOfSite(site: { slug?: unknown; display_name?: unknown; name?: unknown; [k: string]: unknown } | null | undefined): string | null {
  return imgSeoName(site?.slug) ?? imgSeoName(siteNameOf(site));
}

function splitImgUrl(u: string): { origin: string; path: string; tail: string } | null {
  const m = /^(https?:\/\/[^/?#]+)?(\/[^?#]*)([?#].*)?$/i.exec(u);
  return m ? { origin: m[1] ?? '', path: m[2], tail: m[3] ?? '' } : null;
}

/** 별칭 경로 → 정본 경로(`/img/{id}/{key}.jpg`). 별칭이 아니면 입력 그대로. 절대 URL 이면 origin 을 보존한다. */
export function canonicalImgPath(url: string | null | undefined): string {
  const u = String(url ?? '');
  const parts = splitImgUrl(u);
  if (!parts) return u;
  const m = ALIAS_IMG_RE.exec(parts.path);
  if (!m) return u;
  return `${parts.origin}/img/${m[1]}/${m[2]}${m[4]}.jpg${parts.tail}`;
}

/** /img 현장 이미지(정본·별칭) 주소의 site_id(소문자). 아니면 null. */
export function imgSiteIdOf(url: string | null | undefined): string | null {
  const parts = splitImgUrl(canonicalImgPath(url));
  const m = parts ? CANON_IMG_RE.exec(parts.path) : null;
  return m ? m[1].toLowerCase() : null;
}

/** 별칭 주소인가. */
export function isImgAlias(url: string | null | undefined): boolean {
  const parts = splitImgUrl(String(url ?? ''));
  return !!parts && ALIAS_IMG_RE.test(parts.path);
}

/**
 * 정본 /img JPEG 주소(상대·절대) → 이름 별칭 주소. 이름이 없거나 모양이 아니면(webp · /img/cl · 외부 · supabase 직링크) 입력 그대로.
 *   이미 별칭이면 이름을 새로 갈아 끼운다(멱등).
 */
export function imgAliasUrl(url: string | null | undefined, name: unknown): string {
  const raw = String(url ?? '');
  const seo = imgSeoName(name);
  if (!seo) return raw;
  const canon = canonicalImgPath(raw);
  const parts = splitImgUrl(canon);
  if (!parts) return raw;
  const m = CANON_IMG_RE.exec(parts.path);
  if (!m) return raw;
  const key = m[3];
  const shot = /^S[0-9]_[a-z_]+(?=-)/.exec(key)?.[0] ?? '';
  const label = imgSeoName(SCALE_SHOT_LABEL[shot] ?? '');
  const file = [seo, label, IMG_ALIAS_TAIL].filter(Boolean).join('-');
  return `${parts.origin}/img/${m[1]}/${m[2] ? m[2].slice(0, 3) : ''}${encodeURIComponent(file)}${IMG_ALIAS_SEP}${m[2] ? m[2].slice(3) : ''}${key}.jpg${parts.tail}`;
}

/** 현장 id → 별칭 이름 Map 으로 주소를 바꾼다(저장된 주소 — 블로그 커버·채널 변형본 등). Map 에 없으면 입력 그대로. */
export function imgAliasUrlFrom(url: string | null | undefined, names: ReadonlyMap<string, string> | null | undefined): string {
  const raw = String(url ?? '');
  if (!names || names.size === 0) return raw;
  const id = imgSiteIdOf(raw);
  const name = id ? names.get(id) : undefined;
  return name ? imgAliasUrl(raw, name) : raw;
}

/** 리졸버 내부 — 행 → 컷 변환 때 색인 JPEG 의 url 을 별칭으로 낼지. name 이 비면 저장 캡션에서 현장명을 되읽는다. */
export interface ImgAliasOpt { name?: string | null }

function refOf(variants: Record<string, unknown> | null | undefined, key: ScreenVariant, siteId: string, origin: string, aliasName?: string | null): ImgRef | null {
  const raw = variants?.[key];
  const path = publicImgPath(raw);
  if (!path) return null;
  // 다른 현장의 키가 섞여 들어온 행은 버린다(오귀속 방지).
  if (!path.toLowerCase().startsWith(`${IMG_PREFIX}/${siteId.toLowerCase()}/`)) return null;
  const want = key.endsWith('_webp') ? '.webp' : '.jpg';
  if (!path.endsWith(want)) return null;
  const spec = SCREEN_VARIANTS[key];
  // img-names — 색인 JPEG(master·og·thumb)의 절대 URL 만 별칭으로. path(같은 출처 표시용)는 정본 그대로.
  const url = absImgUrl(path, origin);
  return { path, url: spec.type === 'image/jpeg' && aliasName ? imgAliasUrl(url, aliasName) : url, width: spec.width, height: spec.height, type: spec.type };
}

/* ───────────────────────────── 흐린 미리보기 ───────────────────────────── */

const BLUR_RE = /^data:image\/(?:jpeg|webp|png);base64,[A-Za-z0-9+/]+={0,2}$/;

/**
 * 흐린 미리보기 값 검증 — base64 data URI(jpeg·webp·png)만, BLUR_DATA_MAX 이하. 그 밖은 null.
 * ⛔ 이 값은 인라인 style 의 url() 로 나간다 — 따옴표·괄호·공백이 든 값이 통과하면 CSS 주입이 된다. 그래서 문자 집합을 잠근다.
 */
export function blurDataOf(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim();
  return s.length > 0 && s.length <= BLUR_DATA_MAX && BLUR_RE.test(s) ? s : null;
}

/* ───────────────────────────── 행 → 컷 ───────────────────────────── */

export function isSiteShot(x: unknown): x is SiteShot {
  return typeof x === 'string' && (SITE_SHOTS as readonly string[]).includes(x);
}

/**
 * 행 1개 → 컷. 색인 3종(master·og·thumb JPEG)과 alt·caption 이 «전부» 있어야 컷이다 —
 * 하나라도 비면 null(그 컷만 빠진다). alt 없는 이미지를 내보내지 않는다(G-VX1).
 */
export function rowToShot(row: SiteImageRow | null | undefined, origin: string, alias?: ImgAliasOpt | null): GeneratedShot | null {
  if (!row || row.visible === false || !isSiteShot(row.shot)) return null;
  const siteId = String(row.site_id ?? '').trim();
  if (!siteId) return null;
  const alt = String(row.alt_ko ?? '').replace(/\s+/g, ' ').trim();
  const caption = String(row.caption_ko ?? '').replace(/\s+/g, ' ').trim();
  if (!alt || !caption) return null;
  const v = row.variants && typeof row.variants === 'object' ? row.variants : null;
  // img-names — 별칭 이름: 호출부가 준 현장 이름(slug) > 저장 캡션의 현장명. alias 를 안 주면(종전 호출부) 정본 주소 그대로.
  const aliasName = alias ? (imgSeoName(alias.name) ?? imgSeoName(nameFromStoredCaption(row.caption_ko, row.shot))) : null;
  const master = refOf(v, 'master_jpg', siteId, origin, aliasName);
  const og = refOf(v, 'og_1200x630_jpg', siteId, origin, aliasName);
  const thumb = refOf(v, 'thumb_1x1_jpg', siteId, origin, aliasName);
  if (!master || !og || !thumb) return null;
  const blur = blurDataOf(row.blur_data);
  return {
    shot: row.shot,
    alt,
    caption,
    versionHash: String(row.version_hash ?? '').trim() || null,
    master, og, thumb,
    hero21x9: refOf(v, 'hero_21x9_webp', siteId, origin),
    hero4x3: refOf(v, 'hero_4x3_webp', siteId, origin),
    gallery: refOf(v, 'gallery_webp', siteId, origin),
    ...(blur ? { blur } : {}),
  };
}

/** 한 현장의 행들 → 컷 배열(SITE_SHOTS 순 · 컷 중복 제거 · alt 중복 제거 · 최대 4). */
export function rowsToShots(rows: readonly SiteImageRow[] | null | undefined, origin: string, alias?: ImgAliasOpt | null): GeneratedShot[] {
  const out: GeneratedShot[] = [];
  const seenAlt = new Set<string>();
  for (const code of SITE_SHOTS) {
    const row = (rows ?? []).find((r) => r?.shot === code);
    const shot = rowToShot(row, origin, alias);
    if (!shot || seenAlt.has(shot.alt)) continue; // alt 는 이미지마다 고유(§N-2)
    seenAlt.add(shot.alt);
    out.push(shot);
    if (out.length >= MAX_SHOTS) break;
  }
  return out;
}

/**
 * VX-4 §8-3 — 「분양 당시 AI 상상도」 로 남길 컷. 조건을 «전부» 만족하는 행만:
 *   visible === false · hidden_reason === 'completed' · 색인 3종 JPEG·alt 가 온전하다. 캡션은 고정 문구로 갈아 끼운다.
 * 노출 중인 행(visible !== false)·다른 사유(excluded·null)는 절대 들어오지 않는다.
 */
export function archivedShotsOf(rows: readonly SiteImageRow[] | null | undefined, origin: string, alias?: ImgAliasOpt | null): GeneratedShot[] {
  const mine = (rows ?? []).filter((r) => !!r && r.visible === false && r.hidden_reason === HIDDEN_COMPLETED);
  return rowsToShots(mine.map((r) => ({ ...r, visible: true })), origin, alias).map((s) => ({ ...s, caption: ARCHIVED_SHOT_CAPTION }));
}

/* ───────────────────────────── 우선순위(단일 함수) ───────────────────────────── */

export interface ResolveOpts {
  /** 게이트. 닫혀 있으면 rows 를 «보지 않는다». */
  gateOpen: boolean;
  /** 절대 URL 의 origin(SITE_URL). */
  origin: string;
  /** 상세의 위성 히어로 정책(W4-3). 목록은 넘기지 않는다 — licensedHeroPhoto 의 기존 관례. */
  policy?: HeroPolicy;
  /**
   * VX-3 C3 — 그 현장의 site_sat_cards 행. ok === true 이고 site.satellite_image_url 이 자체 경로로 바뀔 때만 위성 등급이 선다.
   * 안 넘기면(기존 호출부 전부) 위성 등급은 꺼진 채 종전과 같다. 게이트(image.site_visible)와 무관하다(기축 목록에서도 돈다).
   */
  satCard?: SatCardRow | null;
  /** VX-4 §8-3 — 내려간 행(상세 조회만 넘긴다). 기축 단계 + hidden_reason='completed' 일 때만 `archived` 로 나간다. */
  archivedRows?: readonly SiteImageRow[] | null;
  /**
   * vx_scale1 E — 그 현장의 규모 사실(site_specs · 레인 a). 있으면 생성 컷의 alt·caption 을 `withScaleText`(단일 문구 도우미)로 갈아 끼운다
   *   (확인된 값만 · 추정값 0 · 사실이 없으면 저장 문구 그대로). 안 넘기면 종전과 같다.
   */
  scale?: SiteScale | null;
}

/**
 * 위성 사진 카드 자격 — ① site_sat_cards.ok === true(행의 site_id 가 있으면 그 현장과 같아야 한다)
 *   ② apt_sites.satellite_image_url 이 …/images/satellite/{그 현장 id}.webp 꼴(다른 현장·다른 버킷 → null)
 *   ③ 위성 히어로 정책이 꺼져 있지 않다(allowSatelliteHero !== false). 하나라도 어긋나면 null.
 */
export function satelliteRefOf(site: SiteImageSiteLike | null | undefined, satCard: SatCardRow | null | undefined, policy?: HeroPolicy): SatelliteRef | null {
  if (!site || !satCard || satCard.ok !== true) return null;
  if (policy?.allowSatelliteHero === false) return null;
  const siteId = String(site.id ?? '').trim().toLowerCase();
  if (!siteId) return null;
  if (satCard.site_id != null && String(satCard.site_id).trim().toLowerCase() !== siteId) return null;
  const path = satRefPathFromLegacyUrl(site.satellite_image_url, siteId);
  if (!path) return null;
  return { path, width: SAT_REF_SIZE, height: SAT_REF_SIZE, blur: blurDataOf(satCard.blur_data) };
}

/** 위성 후보인가(자격 조회 전 1차 거름) — satellite_image_url 이 실려 왔고 자체 경로로 바뀐다. */
export function isSatelliteCandidate(site: SiteImageSiteLike | null | undefined): boolean {
  const siteId = String(site?.id ?? '').trim().toLowerCase();
  return !!siteId && satRefPathFromLegacyUrl(site?.satellite_image_url, siteId) !== null;
}

/**
 * 공식 > 생성 > 위성 사진 카드(VX-3 · satCard 를 받았을 때만) > 카드. 이 트랙의 «유일한» 우선순위 판정.
 * ⚠️ site 에 라이선스 열이 없으면(undefined) 공식은 «없음» 이다(heroLicenseOk 규율) — 그때는 생성·카드로 내려간다.
 */
export function resolveFromRows(
  site: SiteImageSiteLike | null | undefined,
  rows: readonly SiteImageRow[] | null | undefined,
  opts: ResolveOpts,
): ResolvedSiteImage {
  const officialUrl = licensedHeroPhoto(site, opts.policy);
  if (officialUrl) return { kind: 'official', gateOpen: opts.gateOpen, officialUrl, shots: [], hero: null };
  // VX-4 §8-3 · G-VX4-8 — 입주가 시작된 현장은 visible 행이 남아 있어도 «생성 등급» 이 아니다.
  //   적재기가 하루 한 번 visible 을 내리지만, 단계가 바뀐 직후의 공백(최대 24시간)에도 히어로·og·JSON-LD·sitemap 에 상상도가 서지 않게
  //   판정 자체를 여기서 막는다(이중 방어). site 에 lifecycle_stage 가 안 실려 온 호출부(id 만)는 종전과 같다.
  const completed = isCompletedStage(site?.lifecycle_stage);
  const siteId = String(site?.id ?? '').trim().toLowerCase();
  const ofSite = (list: readonly SiteImageRow[] | null | undefined) =>
    (list ?? []).filter((r) => !siteId || String(r?.site_id ?? '').trim().toLowerCase() === siteId);
  // img-names — 색인 JPEG 는 이름 별칭 주소로(slug > 표시명 > 저장 캡션의 현장명). 리졸버를 지나는 모든 출력(og·JSON-LD·sitemap·<img src>·썸네일)이 같은 주소를 낸다.
  const alias: ImgAliasOpt = { name: imgSeoNameOfSite(site) };
  if (opts.gateOpen && !completed) {
    const base = rowsToShots(ofSite(rows), opts.origin, alias);
    const scaleName = opts.scale ? siteNameOf(site) : null;
    const shots = opts.scale ? base.map((s) => withScaleText(s, scaleName, opts.scale)) : base;
    const badge = opts.scale ? scaleBadgeText(opts.scale) : null;
    if (shots.length > 0) return { kind: 'generated', gateOpen: true, officialUrl: null, shots, hero: shots[0], ...(badge ? { scaleBadge: badge } : {}) };
  }
  // 「분양 당시 AI 상상도」 — 게이트 열림 · 기축 단계 · 공식 사진 없음(위에서 이미 돌아갔다) · 내려간 사유 completed 인 행만.
  const archived = opts.gateOpen && completed && opts.archivedRows ? archivedShotsOf(ofSite(opts.archivedRows), opts.origin, alias) : [];
  const tail = archived.length > 0 ? { archived } : {};
  const satellite = opts.satCard ? satelliteRefOf(site, opts.satCard, opts.policy) : null;
  if (satellite) return { kind: 'satellite', gateOpen: opts.gateOpen, officialUrl: null, shots: [], hero: null, satellite, ...tail };
  return { kind: 'card', gateOpen: opts.gateOpen, officialUrl: null, shots: [], hero: null, ...tail };
}

/** 빈 결과(조회 실패·부재) — 기존 체인 그대로. */
export const CARD_RESOLVED: ResolvedSiteImage = Object.freeze({ kind: 'card', gateOpen: false, officialUrl: null, shots: [], hero: null }) as ResolvedSiteImage;

/* ───────────────────────────── 표면별 출력 ───────────────────────────── */

export type MediaSlot = 'hero' | 'hero_mobile' | 'gallery' | 'thumb' | 'og';

/** 컷 1장 → MEDIA_IFACE 모양. jpg 는 색인용 절대 URL, webp 는 표시용 상대경로. */
export function shotToMedia(shot: GeneratedShot, slot: MediaSlot = 'gallery'): SiteMedia {
  const base = { kind: 'generated' as const, alt: shot.alt, caption: shot.caption, shot: shot.shot, ...(shot.blur ? { blur: shot.blur } : {}) };
  if (slot === 'thumb') return { ...base, jpg: shot.thumb.url, width: shot.thumb.width, height: shot.thumb.height };
  if (slot === 'og') return { ...base, jpg: shot.og.url, width: shot.og.width, height: shot.og.height };
  const webp = slot === 'hero' ? shot.hero21x9 : slot === 'hero_mobile' ? shot.hero4x3 : shot.gallery;
  return {
    ...base,
    jpg: shot.master.url,
    ...(webp ? { webp: webp.path } : {}),
    width: webp ? webp.width : shot.master.width,
    height: webp ? webp.height : shot.master.height,
  };
}

/** 리졸버 결과 → 히어로 미디어 1장. 생성이 아니면 null(호출부가 기존 체인을 그린다). */
export function heroMediaOf(r: ResolvedSiteImage | null | undefined, slot: MediaSlot = 'hero'): SiteMedia | null {
  return r?.kind === 'generated' && r.hero ? shotToMedia(r.hero, slot) : null;
}

/**
 * VX-3 D3 — 히어로용 picture source(있는 파생본만): ≥768px = hero_21x9 webp(1920×823) · 그 밖 = hero_4x3 webp(1440×1080).
 * 640·960 파생본은 «없다» — 없는 주소를 짓지 않는다. 한쪽이라도 없으면 빈 배열(호출부는 webp 한 장으로 선다).
 */
export function heroSourcesOf(shot: GeneratedShot | null | undefined): NonNullable<SiteMedia['sources']> {
  if (!shot || !shot.hero21x9 || !shot.hero4x3) return [];
  return [
    { srcSet: shot.hero21x9.path, media: '(min-width: 768px)', type: shot.hero21x9.type, width: shot.hero21x9.width, height: shot.hero21x9.height },
    { srcSet: shot.hero4x3.path, type: shot.hero4x3.type, width: shot.hero4x3.width, height: shot.hero4x3.height },
  ];
}

/**
 * VX-3 C3 — 리졸버 결과 → 화면 미디어 1장. «생성·위성 둘 다» 낸다(heroMediaOf 는 생성만 — 종전 의미 그대로).
 *   · generated → shotToMedia(hero, slot) 그대로(+ blur). slot 'hero' 는 sources(21:9·4:3 webp) 도 싣는다.
 *   · satellite → kind 'satellite' · jpg 자리에 /img-sat/legacy/{id}.webp(noindex 상대경로 — 색인 대상이 아니다) ·
 *                 alt 「{이름} 현재 위성사진」 · caption = credit = 출처 문구(lib/attribution/vworld) · 1024². slot 'og' 는 null(색인 경로에 위성을 내지 않는다).
 *   · official·card → null(호출부가 기존 체인을 그린다).
 */
export function mediaOf(r: ResolvedSiteImage | null | undefined, slot: MediaSlot = 'gallery', opts?: { name?: string | null }): SiteMedia | null {
  if (!r) return null;
  if (r.kind === 'generated' && r.hero) {
    const m = shotToMedia(r.hero, slot);
    const sources = slot === 'hero' ? heroSourcesOf(r.hero) : [];
    return sources.length ? { ...m, sources } : m;
  }
  if (r.kind === 'satellite' && r.satellite && slot !== 'og') {
    const s = r.satellite;
    return {
      kind: 'satellite', jpg: s.path, alt: satelliteAltOf(opts?.name), caption: SATELLITE_CREDIT_TEXT, credit: SATELLITE_CREDIT_TEXT,
      width: s.width, height: s.height, ...(s.blur ? { blur: s.blur } : {}),
    };
  }
  return null;
}

/** VX-4 §8-3 — 「분양 당시」 줄의 화면 미디어(갤러리 판형 · 고정 캡션). 없으면 빈 배열. 히어로·색인 출력과 무관하다. */
export function archivedMediaOf(r: ResolvedSiteImage | null | undefined): SiteMedia[] {
  return (r?.archived ?? []).map((s) => ({ ...shotToMedia(s, 'gallery'), caption: ARCHIVED_SHOT_CAPTION }));
}

/** 리졸버 결과 → 갤러리 미디어(최대 4). 생성이 아니면 빈 배열. */
export function galleryMediaOf(r: ResolvedSiteImage | null | undefined): SiteMedia[] {
  return r?.kind === 'generated' ? r.shots.map((s) => shotToMedia(s, 'gallery')) : [];
}

/** 목록 썸네일 주소(1:1 JPEG 절대 URL). 생성이 아니면 null. */
export function thumbUrlOf(r: ResolvedSiteImage | null | undefined): string | null {
  return r?.kind === 'generated' && r.hero ? r.hero.thumb.url : null;
}

/** og:image — og_1200x630_jpg + width/height/alt(§N-3). 생성이 아니면 null → 호출부는 기존 선택 그대로. */
export function ogImageOf(r: ResolvedSiteImage | null | undefined): { url: string; width: number; height: number; alt: string; type: string } | null {
  if (r?.kind !== 'generated' || !r.hero) return null;
  const { og } = r.hero;
  return { url: og.url, width: og.width, height: og.height, alt: r.hero.alt, type: og.type };
}

/**
 * JSON-LD ImageObject 배열(§N-4) — contentUrl(자체 도메인 JPEG)·caption·creditText·representativeOfPage(히어로 컷 = S1 만 true).
 * 생성이 아니면 빈 배열(호출부 출력 불변).
 */
export function jsonLdImagesOf(r: ResolvedSiteImage | null | undefined): Record<string, unknown>[] {
  if (r?.kind !== 'generated') return [];
  return r.shots.map((s, i) => ({
    '@type': 'ImageObject',
    contentUrl: s.master.url,
    url: s.master.url,
    width: s.master.width,
    height: s.master.height,
    name: s.alt,
    caption: s.caption,
    creditText: AI_CREDIT_TEXT,
    // 대표 = 히어로 컷(S1 이 있으면 언제나 S1 — shots 는 S1→S4 순). 페이지당 정확히 1개.
    representativeOfPage: i === 0,
  }));
}

/* ───────────────────────────── 목록 썸네일 ───────────────────────────── */

const GEN_URL_RE = new RegExp(`/img/${UUID}/S[0-9]_[a-z_]+-[a-z0-9_]+-[A-Za-z0-9]+[.](?:jpg|webp)$`);

/** VX-3 채널 변형본 `/img/{site_id}/ch/{channel}-{shot}-{ratio}-{hash}.jpg` — 상상도에서 구운 파생본(레인 A 키 규칙). */
const CH_URL_RE = new RegExp(`/img/${UUID}/ch/([a-z0-9]+)-S[0-9]_[a-z_]+-[a-z0-9]+-[A-Za-z0-9]+[.]jpg$`);

function imgPathOf(src: string | null | undefined): string | null {
  const u = String(src ?? '').trim();
  if (!u) return null;
  // img-names — 별칭 주소는 정본으로 되돌려 판정한다(파일명 규칙이 같다).
  try { return canonicalImgPath(new URL(u, CANONICAL_ORIGIN).pathname); } catch { return null; }
}

/** 채널 변형본 주소면 채널 이름(`card`·`thumb`…), 아니면 null. */
export function channelOfImgUrl(src: string | null | undefined): string | null {
  const p = imgPathOf(src);
  const m = p ? CH_URL_RE.exec(p) : null;
  return m ? m[1] : null;
}

/**
 * 이 주소가 생성 현장 이미지인가 — 썸네일 컴포넌트가 figure·캡션을 붙일지 정한다.
 *   `/img/{site_id}/{shot}-…`(L2 판형) + VX-3 R1 `/img/{site_id}/ch/…`(채널 변형본 — apt_sites.card_image_url 에 실린다).
 */
export function isGeneratedImgUrl(src: string | null | undefined): boolean {
  const p = imgPathOf(src);
  return !!p && (GEN_URL_RE.test(p) || CH_URL_RE.test(p));
}

/** VX-3 R1 — 제목띠가 구워진 상상도 카드(`ch/card-`·`ch/cafe-`)인가. apt_sites.card_image_url 의 새 값이 이 꼴이다. */
export function isChannelCardUrl(src: string | null | undefined): boolean {
  const ch = channelOfImgUrl(src);
  return ch === 'card' || ch === 'cafe';
}

/**
 * «글자가 구워진 카드» 인가 — 행 썸네일(40~64px)·사진 자리에 «사진» 으로 쓰지 않는 주소.
 *   옛 네이비 카드(`/api/og-apt` · `…/images/card/{id}-1x1.webp`) + VX-3 R1 상상도 카드(`/img/{id}/ch/card-…`).
 *   R1 이 card_image_url 을 바꿔도 「카드는 사진이 아니다」 로 거르던 자리(KdTable·홈·지역 표)가 종전과 같게 서도록 한 곳에서 판정한다.
 */
export function isBakedCardUrl(src: string | null | undefined): boolean {
  const u = String(src ?? '').trim();
  if (!u) return false;
  return u.includes('/api/og-apt') || u.includes('/images/card/') || isChannelCardUrl(u);
}

/** 목록 썸네일용 고지 문구(§N-2 figcaption 과 같은 꼬리). 행에 caption_ko 를 싣지 않는 기존 로더용. */
export function generatedThumbCaption(name: string | null | undefined): string {
  const n = String(name ?? '').replace(/\s+/g, ' ').trim();
  return `${n ? `${n} ` : ''}상상도 · AI 생성 이미지로 실제와 다를 수 있음`;
}

/* ───────────────────────────── sitemap ───────────────────────────── */

/** XML 텍스트 이스케이프(5종) + XML 1.0 금지 제어문자 제거. */
export function xmlEscape(s: string): string {
  return String(s ?? '')
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F￾￿]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * `<loc>` 용 — 맨 `&` 만 `&amp;` 로(이미 엔티티인 것은 그대로). 특수문자가 없는 URL 은 바이트 동일.
 * 실측(2026-10-12): 블로그 slug `…-m&a-…` 한 건이 sitemap/8 전체를 비정형 XML 로 만들고 있었다.
 */
export function xmlLoc(url: string): string {
  return String(url ?? '').replace(/&(?!(?:amp|lt|gt|quot|apos|#\d+|#x[0-9a-fA-F]+);)/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** sitemap 에 실을 이미지 주소(자체 도메인 JPEG master · 최대 4). 생성이 아니면 빈 배열. */
export function sitemapImageLocsOf(r: ResolvedSiteImage | null | undefined): string[] {
  return r?.kind === 'generated' ? r.shots.slice(0, MAX_SHOTS).map((s) => s.master.url) : [];
}

/** 사이트맵 이미지 1장 — 주소만(종전) 또는 주소 + 제목·캡션(img-names: image:title · image:caption). */
export type SitemapImageItem = string | { loc: string; title?: string | null; caption?: string | null };

export function sitemapImageLocOf(it: SitemapImageItem | null | undefined): string {
  return typeof it === 'string' ? it : String(it?.loc ?? '');
}

/** sitemap 이미지(주소 + 제목 = alt · 캡션 = figcaption). 생성이 아니면 빈 배열. 주소는 sitemapImageLocsOf 와 같다. */
export function sitemapImageItemsOf(r: ResolvedSiteImage | null | undefined): Array<{ loc: string; title: string; caption: string }> {
  return r?.kind === 'generated' ? r.shots.slice(0, MAX_SHOTS).map((s) => ({ loc: s.master.url, title: s.alt, caption: s.caption })) : [];
}

const clip = (v: unknown) => String(v ?? '').replace(/\s+/g, ' ').trim();

/** `<image:image><image:loc>…</image:loc>[<image:title>…<image:caption>…]</image:image>` 조각들(들여쓰기 4칸 — <url> 자식). 없으면 ''. */
export function sitemapImageXml(locs: readonly SitemapImageItem[] | null | undefined): string {
  const safe = (locs ?? []).filter((it) => /^https:\/\/[^\s<>"']+\/img\/[^\s<>"']+\.jpg$/.test(sitemapImageLocOf(it))).slice(0, MAX_SHOTS);
  return safe.map((it) => {
    const u = sitemapImageLocOf(it);
    const title = typeof it === 'string' ? '' : clip(it.title);
    const caption = typeof it === 'string' ? '' : clip(it.caption) || title;
    const extra = `${title ? `      <image:title>${xmlEscape(title)}</image:title>\n` : ''}${caption ? `      <image:caption>${xmlEscape(caption)}</image:caption>\n` : ''}`;
    return `    <image:image>\n      <image:loc>${xmlEscape(u)}</image:loc>\n${extra}    </image:image>\n`;
  }).join('');
}

export const SITEMAP_IMAGE_XMLNS = 'xmlns:image="http://www.google.com/schemas/sitemap-image/1.1"';
